import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useShopifyCRUDActions } from '@mm/state/actions/ShopifyCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectShopifyConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { prepareConnectionsMessage } from '../KlaviyoIntegrations/use-connect-klaviyo';

export const useConnectShopify = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector((state: StoreState) => state.shopify.accounts);
  const shopifyConnections = useSelector(selectShopifyConnections);
  const { openConnectShopifyModal } = useModalActions();

  const {
    getAccounts,
    revokeAccessToken,
    revoke: revokeToken,
    getRevenues,
  } = useShopifyCRUDActions();

  const { retrieveAll, create, deleteConnection } = useConnectionCRUDActions();

  // INFO: This is commented out because due to disabled oauth flow
  // const { connect: connectOauth } = useGuardedConnectOAuthToken(
  //   'nativeIntegrations',
  //   landmineNames.SHOPIFY_UPGRADE,
  //   'shopifyConnect',
  //   'shopifyTokenReceived',
  //   fetchOAuthUrl,
  //   async () => {
  //     const accountsResponse = await getShopifyAccounts();
  //     const { id } = accountsResponse[0];

  //     if (!shopifyConnections.length) {
  //       createConnection({ tokenId: id, tokenType: 'oauth_token' });
  //     } else if (
  //       (shopifyConnections || []).find(conn => conn.accessToken?.id !== id)
  //     ) {
  //       shopifyConnections.forEach(async connection => {
  //         await update(facebookPageId, {
  //           id: connection.id,
  //           shopifyParams: { tokenId: id },
  //         });
  //       });
  //     }
  //   },
  //   { width: 600, height: 600 },
  // );

  // const fetchOAuthUrl = useCallback(
  //   async (shop: string) => {
  //     const link = await getOauthUrl(shop, facebookPageId);
  //     return link;
  //   },
  //   [getOauthUrl],
  // );

  const connectionsMessage = prepareConnectionsMessage(
    shopifyConnections,
    'Shopify',
  );

  const createConnection = async (
    shopifyParams: ConnectionModel['shopifyParams'],
  ) =>
    create(facebookPageId, {
      name: 'Shopify Connection',
      connectionKind: 'shopify',
      shopifyParams,
    });

  const connectAccessToken = async (shop: string, accessToken: string) => {
    const webhook = await createConnection({
      tokenType: 'Shopify::AccessToken',
      token: { accessToken, shop },
    });
    getShopifyConnections();
    getShopifyAccounts();
    return webhook;
  };

  const connectShopify = () => {
    const account = accounts?.[0];
    const params: Parameters<typeof openConnectShopifyModal>[0] = {
      onConnect: connectAccessToken,
    };
    if (account && account.shop) {
      params.defaultShop = account.shop;
    }
    if (account && 'accessToken' in account) {
      params.defaultAccessToken = account.accessToken;
    }

    openConnectShopifyModal(params);
  };

  const getShopifyAccounts = async () => getAccounts(facebookPageId);

  const getShopifyConnections = () => {
    retrieveAll(facebookPageId, {
      type: 'shopify',
      pageSize: 200,
    });
  };

  const revoke = async (id: number) => {
    await revokeToken(id);
    shopifyConnections.forEach(async connection => {
      await deleteConnection(facebookPageId, connection as ConnectionModel);
    });
  };

  return {
    connectionsMessage,
    getShopifyConnections,
    getShopifyAccounts,
    connectShopify,
    revoke,
    getRevenues,
    revokeAccessToken,
  };
};
